import type { InboxItem, WaitingItem, WaitingKind } from './types';

/**
 * „Videné“ pre „Čaká na mňa“ je jedno číslo: čas najnovšej položky, ktorú
 * človek videl. Rovnako to robí zvonček aktivity.
 *
 * Položky sa na serveri nikam nezapisujú (vyrátajú sa z komentárov, ticketov,
 * scenárov a ankiet), takže „prečítané“ sa nedá pripísať k riadku. Server si
 * preto od migrácie 0075 pamätá iba to jedno číslo na človeka
 * (`/api/inbox/waiting/seen`), aby to, čo človek prečítal na telefóne,
 * nesvietilo na počítači ako nové ešte raz.
 *
 * localStorage ostáva ako miestna kópia: odznak sa ukáže hneď pri štarte bez
 * čakania na server, a kým server tabuľku nemá (`synced: false`), je to jediná
 * pamäť — presne ako predtým. Platí vždy väčšia z dvoch hodnôt
 * (`mergeWaitingSeen`), takže ani jedna strana nevie „videné“ vrátiť späť.
 */
export const waitingSeenKey = (userId: number | undefined): string =>
  `ww_waiting_seen:${userId ?? 'anonymous'}`;

/** Ohlási ostatným častiam tej istej karty, že sa „videné“ posunulo. */
export const WAITING_SEEN_EVENT = 'ww-waiting-seen';

export const readWaitingSeen = (userId: number | undefined): number => {
  try {
    const value = Number(localStorage.getItem(waitingSeenKey(userId)) || 0);
    return Number.isFinite(value) ? value : 0;
  } catch {
    // Súkromné okno alebo zablokované úložisko: všetko sa ukáže ako nové,
    // čo je lepšie, než aby sa niečo ticho stratilo.
    return 0;
  }
};

export const writeWaitingSeen = (userId: number | undefined, value: number): void => {
  try {
    localStorage.setItem(waitingSeenKey(userId), String(value));
  } catch {
    // Pozri readWaitingSeen.
  }
};

/** Serverová značka (ISO) ako číslo; chýbajúca alebo pokazená je 0. */
export const parseSeenUntil = (value: string | null | undefined): number => {
  const parsed = value ? Date.parse(value) : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Miestna a serverová značka dokopy — vždy tá neskoršia. */
export const mergeWaitingSeen = (local: number, server: string | null | undefined): number =>
  Math.max(local, parseSeenUntil(server));

const stamp = (item: WaitingItem): number => {
  const value = Date.parse(item.createdAt);
  return Number.isNaN(value) ? 0 : value;
};

export const newestWaiting = (items: WaitingItem[]): number =>
  items.reduce((max, item) => Math.max(max, stamp(item)), 0);

export const isWaitingNew = (item: WaitingItem, seenAt: number): boolean => stamp(item) > seenAt;

/**
 * Koľko nového čaká — bez toho, čo už ukazuje odznak push schránky.
 *
 * Komentár pod mojím tiketom príde aj ako push riadok, aj ako položka tu. Oba
 * vedú na tú istú adresu (`taskPushUrl`), takže podľa nej sa dajú spárovať.
 * Bez toho by jeden komentár zdvihol odznak o dva a po otvorení by jeden z
 * nich zostal visieť.
 */
export const countUnseenWaiting = (
  items: WaitingItem[],
  seenAt: number,
  unreadPush: Array<Pick<InboxItem, 'url' | 'readAt'>> = [],
): number => {
  const covered = new Set(
    unreadPush.filter((row) => row.readAt === null && row.url).map((row) => row.url as string),
  );
  return items.filter((item) => isWaitingNew(item, seenAt) && !covered.has(item.url)).length;
};

export const WAITING_KIND_LABEL: Record<WaitingKind, string> = {
  mention: 'Zmienka',
  comment: 'Komentár',
  testing: 'Testovanie',
  tests: 'Testy',
  poll: 'Anketa',
  stale: 'Zaseknuté',
  leads: 'Leady',
};
